<template>
  <div class="collections-tab" v-loading="loading">
    <!-- KPI summary -->
    <div class="ct-kpis mb-4">
      <div class="ct-kpi">
        <span class="ct-kpi__label">Facturas pendientes</span>
        <span class="ct-kpi__value">{{ pendingInvoices.length }}</span>
      </div>
      <div class="ct-kpi">
        <span class="ct-kpi__label">Monto pendiente</span>
        <span class="ct-kpi__value ct-kpi__value--red"
          >${{ formatNum(totalPending) }}</span
        >
      </div>
      <div class="ct-kpi">
        <span class="ct-kpi__label">Con abonos parciales</span>
        <span class="ct-kpi__value ct-kpi__value--green">{{
          partialCount
        }}</span>
      </div>
    </div>

    <!-- Filters -->
    <div class="dir-toolbar">
      <div class="dir-toolbar-search">
        <el-input
          v-model="search"
          placeholder="Buscar por factura o cliente..."
          suffix-icon="el-icon-search"
          size="small"
          clearable
        />
      </div>
      <FilterBetweenDatesSelect
        :defaultMonthValue="true"
        @onAsyncDataForDates="onAsyncDataForDates"
      />
      <div class="dir-toolbar-right"></div>
    </div>

    <!-- Empty state -->
    <div v-if="!loading && filteredInvoices.length === 0" class="ct-empty">
      <i class="el-icon-circle-check"></i>
      <p>
        {{
          search
            ? "Sin resultados para la búsqueda"
            : "No hay facturas pendientes de cobro"
        }}
      </p>
    </div>

    <template v-if="filteredInvoices.length > 0">
      <!-- ── DESKTOP: tabla ── -->
      <div class="d-none d-md-block">
        <div class="table-items-header ct-header">
          <b-row>
            <b-col md="2"><h6 class="h6 ml-2">Factura</h6></b-col>
            <b-col md="3"><h6 class="h6">Cliente</h6></b-col>
            <b-col md="2"><h6 class="h6">Fecha</h6></b-col>
            <b-col md="2"><h6 class="h6">Total</h6></b-col>
            <b-col md="1"><h6 class="h6">Cobrado</h6></b-col>
            <b-col md="1"><h6 class="h6">Saldo</h6></b-col>
            <b-col md="1"></b-col>
          </b-row>
        </div>

        <div
          v-for="inv in filteredInvoices"
          :key="inv.id"
          class="card ct-card"
          @click="openPreview(inv)"
        >
          <div class="card-body ct-card__body">
            <b-row class="align-items-center">
              <b-col md="2">
                <span class="ct-invoice-num">{{
                  inv.number_invoice || inv.sequence
                }}</span>
              </b-col>
              <b-col md="3">
                <span class="ct-client">{{
                  inv.client && (inv.client.business_name || inv.client.name)
                }}</span>
              </b-col>
              <b-col md="2">
                <span class="ct-meta">{{ inv.emission_date }}</span>
              </b-col>
              <b-col md="2">
                <span class="ct-amount">${{ formatNum(inv.total) }}</span>
              </b-col>
              <b-col md="1">
                <span class="ct-amount ct-amount--paid"
                  >${{ formatNum(inv.total_paid || 0) }}</span
                >
              </b-col>
              <b-col md="1">
                <span class="ct-amount ct-amount--balance"
                  >${{ formatNum(inv.balance) }}</span
                >
              </b-col>
              <b-col
                md="1"
                class="d-flex align-items-center justify-content-end"
              >
                <el-tooltip content="Agregar pago" placement="top">
                  <button class="ct-action-btn" @click.stop="openPayment(inv)">
                    <i class="el-icon-coin"></i>
                  </button>
                </el-tooltip>
              </b-col>
            </b-row>
            <div
              v-if="inv.payment_status === 'partial'"
              class="ct-partial-hint"
            >
              <i class="el-icon-info"></i>
              Tiene {{ inv.payments_count || "varios" }} abono(s) — saldo
              pendiente:
              <strong>${{ formatNum(inv.balance) }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- ── MOBILE: tarjetas ── -->
      <div class="ct-mobile-list d-md-none">
        <div
          v-for="inv in filteredInvoices"
          :key="'m-' + inv.id"
          class="ct-mobile-card"
          @click="openPreview(inv)"
        >
          <!-- Cabecera -->
          <div class="ct-mobile-card__head">
            <span class="ct-mobile-card__num">{{
              inv.number_invoice || inv.sequence
            }}</span>
            <span class="ct-mobile-card__date">{{ inv.emission_date }}</span>
          </div>

          <!-- Cliente -->
          <div class="ct-mobile-card__client">
            {{ inv.client && (inv.client.business_name || inv.client.name) }}
          </div>

          <!-- Montos -->
          <div class="ct-mobile-card__amounts">
            <div class="ct-mobile-card__amount-row">
              <span class="ct-mobile-card__amount-label">Total</span>
              <span class="ct-mobile-card__amount"
                >${{ formatNum(inv.total) }}</span
              >
            </div>
            <div class="ct-mobile-card__amount-row">
              <span class="ct-mobile-card__amount-label">Cobrado</span>
              <span class="ct-mobile-card__amount ct-mobile-card__amount--paid"
                >${{ formatNum(inv.total_paid || 0) }}</span
              >
            </div>
            <div
              class="ct-mobile-card__amount-row ct-mobile-card__amount-row--total"
            >
              <span class="ct-mobile-card__amount-label">Saldo pendiente</span>
              <span
                class="ct-mobile-card__amount ct-mobile-card__amount--balance"
                >${{ formatNum(inv.balance) }}</span
              >
            </div>
          </div>

          <!-- Footer -->
          <div class="ct-mobile-card__footer">
            <span
              v-if="inv.payment_status === 'partial'"
              class="ct-mobile-card__partial-tag"
            >
              <i class="el-icon-info"></i>
              {{ inv.payments_count || "Varios" }} abono(s)
            </span>
            <span
              v-else
              class="ct-mobile-card__partial-tag ct-mobile-card__partial-tag--empty"
            ></span>
            <button
              class="ct-mobile-card__pay-btn"
              @click.stop="openPayment(inv)"
            >
              <i class="el-icon-coin"></i> Agregar pago
            </button>
          </div>
        </div>
      </div>
    </template>

    <PaymentModal
      v-if="paymentInvoice"
      :invoice="paymentInvoice"
      @close="paymentInvoice = null"
      @payment-registered="onPaymentDone"
    />

    <InvoicePreview
      v-if="selectedInvoice"
      :invoice="selectedInvoice"
      :showModal="true"
      :status="selectedInvoice ? selectedInvoice.status : ''"
      @close="selectedInvoice = null"
    />
  </div>
</template>

<script>
import PaymentModal from "./PaymentModal";
import FilterBetweenDatesSelect from "../../../../components/FilterBetweenDatesSelect";
import InvoicePreview from "../../invoices/components/InvoicePreview";
import invoiceService from "../../invoices/services/invoiceService";

export default {
  name: "CollectionsTab",
  components: { PaymentModal, FilterBetweenDatesSelect, InvoicePreview },
  data() {
    return {
      paymentInvoice: null,
      selectedInvoice: null,
      search: "",
      invoices: [],
      loading: false,
      searchDates: null
    };
  },
  computed: {
    pendingInvoices() {
      return this.invoices.filter(
        inv => inv.payment_status !== "paid" && inv.status !== "nuled"
      );
    },
    filteredInvoices() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.pendingInvoices;
      return this.pendingInvoices.filter(inv => {
        const num = (inv.number_invoice || inv.sequence || "").toLowerCase();
        const client = (
          (inv.client && (inv.client.business_name || inv.client.name)) ||
          ""
        ).toLowerCase();
        return num.includes(q) || client.includes(q);
      });
    },
    totalPending() {
      return this.pendingInvoices.reduce(
        (s, inv) => s + (parseFloat(inv.balance) || parseFloat(inv.total) || 0),
        0
      );
    },
    partialCount() {
      return this.pendingInvoices.filter(
        inv => inv.payment_status === "partial"
      ).length;
    }
  },
  methods: {
    async loadInvoices() {
      try {
        this.loading = true;
        const params = this.searchDates ? { ...this.searchDates } : {};
        const res = await invoiceService.getInvoices({ params });
        if (res && res.data && res.data.success) {
          this.invoices = res.data.data || [];
        }
      } catch (error) {
        /* silent */
      } finally {
        this.loading = false;
      }
    },
    onAsyncDataForDates({ searchDates }) {
      this.searchDates = searchDates;
      this.invoices = [];
      this.loadInvoices();
    },
    openPayment(invoice) {
      this.paymentInvoice = invoice;
    },
    async openPreview(invoice) {
      try {
        const res = await invoiceService.getInvoceId(invoice.id);
        if (res.data.success) {
          this.selectedInvoice = res.data.data;
        } else {
          this.selectedInvoice = invoice;
        }
      } catch {
        this.selectedInvoice = invoice;
      }
    },
    async onPaymentDone({ invoiceId } = {}) {
      this.paymentInvoice = null;
      this.$emit("payment-registered");
      if (invoiceId) {
        await this.refreshInvoice(invoiceId);
      } else {
        this.loadInvoices();
      }
    },
    async refreshInvoice(invoiceId) {
      try {
        const res = await invoiceService.getInvoceId(invoiceId);
        if (res.data && res.data.data) {
          const idx = this.invoices.findIndex(i => i.id === invoiceId);
          if (idx !== -1) {
            this.$set(this.invoices, idx, res.data.data);
          }
        }
      } catch (e) {
        this.loadInvoices();
      }
    },
    formatNum(val) {
      if (val === null || val === undefined) return "0.00";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #2d2b55;
$accent: #3ecfb4;
$red: #ef4444;
$green: #1d9e75;
$border: #eef0f8;
$bg: #f7f8fb;

// ── KPI summary ───────────────────────────────────────────────────
.ct-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
}

.ct-kpi {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 480px) {
    padding: 10px 12px;
  }

  &__label {
    font-size: 10px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    line-height: 1.3;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: $primary;

    @media (max-width: 480px) {
      font-size: 18px;
    }

    &--red {
      color: $red;
    }
    &--green {
      color: $green;
    }
  }
}

// ── Desktop header row ─────────────────────────────────────────────
.ct-header {
  margin-bottom: 0;
}

// ── Desktop cards ──────────────────────────────────────────────────
.ct-card {
  border: 1px solid $border;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 12px rgba(83, 74, 183, 0.08);
  }

  &__body {
    padding: 12px 16px;
  }
}

.ct-invoice-num {
  font-size: 13px;
  font-weight: 700;
  color: $primary;
}
.ct-client {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
}
.ct-meta {
  font-size: 12px;
  color: #999;
}

.ct-amount {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  &--paid {
    color: $green;
  }
  &--balance {
    color: $red;
  }
}

.ct-action-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: $primary;
  transition: background 0.15s;
  &:hover {
    background: #f0f2f7;
  }
}

.ct-partial-hint {
  margin-top: 8px;
  font-size: 11px;
  color: $green;
  background: #e8f8f3;
  border-radius: 6px;
  padding: 5px 10px;
  display: inline-block;
  i {
    margin-right: 4px;
  }
}

// ── Mobile cards ───────────────────────────────────────────────────
.ct-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ct-mobile-card {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: box-shadow 0.15s;
  &:active {
    box-shadow: 0 2px 10px rgba($primary, 0.08);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__num {
    font-size: 13px;
    font-weight: 700;
    color: $primary;
    font-family: monospace;
  }

  &__date {
    font-size: 12px;
    color: #aaa;
  }

  &__client {
    font-size: 13px;
    font-weight: 600;
    color: $primary;
    margin-bottom: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__amounts {
    background: $bg;
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;
  }

  &__amount-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;

    &--total {
      border-top: 1px solid $border;
      padding-top: 6px;
      margin-top: 2px;
      font-weight: 700;
    }
  }

  &__amount-label {
    font-size: 11px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__amount {
    font-size: 13px;
    font-weight: 600;
    color: $primary;
    &--paid {
      color: $green;
    }
    &--balance {
      color: $red;
      font-weight: 700;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__partial-tag {
    font-size: 11px;
    color: $green;
    background: #e8f8f3;
    border-radius: 6px;
    padding: 3px 8px;
    display: flex;
    align-items: center;
    gap: 4px;

    &--empty {
      background: transparent;
      padding: 0;
    }
  }

  &__pay-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    background: $accent;
    border: none;
    border-radius: 8px;
    padding: 6px 14px;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
    &:hover {
      background: darken($accent, 6%);
    }
    i {
      font-size: 13px;
    }
  }
}

// ── Empty state ───────────────────────────────────────────────────
.ct-empty {
  text-align: center;
  padding: 60px 20px;
  color: #aaa;

  i {
    font-size: 36px;
    color: $green;
    display: block;
    margin-bottom: 12px;
  }
  p {
    font-size: 14px;
    margin: 0;
  }
}
</style>
